/* global __initial_auth_token, __app_id, __firebase_config */

import React, { useState, useEffect, useRef } from 'react';
import { appTypography } from '../theme/appTypography';
import {
    Box, Paper, Typography, Button, TextField, Grid, List, ListItem, ListItemButton,
    ListItemIcon, ListItemText, Stepper, Step, StepLabel, Alert, CircularProgress,
    Checkbox, FormControlLabel, Collapse, IconButton, Divider, useMediaQuery
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
    AssignmentTurnedIn as AssignmentTurnedInIcon,
    DirectionsCar as CarIcon,
    DirectionsBus as BusIcon,
    Apartment as ApartmentIcon,
    Chat as ChatIcon,
    Send as SendIcon,
    Logout as LogoutIcon,
    VideoLibrary as VideoLibraryIcon,
    Rule as RuleIcon,
    LiveHelp as LiveHelpIcon,
    AirportShuttle as ShuttleIcon,
    WarningAmber as WarningIcon
} from '@mui/icons-material';

// --- IMPORT AXIOS CONFIG (Added per instructions) ---
import api from '../axiosConfig';
import PhoneInputField, { COUNTRY_PHONE_CODES } from './PhoneInputField';

// Firebase Imports
import { initializeApp, getApps, getApp } from "firebase/app";
import { getAuth, signInAnonymously, onAuthStateChanged, signInWithCustomToken } from "firebase/auth";
import { getFirestore, doc, addDoc, writeBatch, collection, onSnapshot, serverTimestamp, query, orderBy, updateDoc } from "firebase/firestore";


// --- Firebase Configuration & Initialization ---
const firebaseConfig = typeof __firebase_config !== 'undefined'
    ? JSON.parse(__firebase_config)
    : {
        apiKey: "AIzaSyDArzbZ8gvcfBWhraTOtiEPrmdEAaTz5HY",
        authDomain: "multichannel-inquiry-system.firebaseapp.com",
        projectId: "multichannel-inquiry-system",
        storageBucket: "multichannel-inquiry-system.appspot.com",
        messagingSenderId: "240171385312",
        appId: "1:240171385312:web:16ba555d363cf85bc62b85",
        measurementId: "G-DF58M5DFXW"
    };


let app;
if (!getApps().length) {
    app = initializeApp(firebaseConfig);
} else {
    app = getApp();
}
const firestoreDbInstance = getFirestore(app);
const firebaseAuthInstance = getAuth(app);


// --- Material-UI Theme ---
const inquiryFlowTheme = createTheme({
    palette: {
        primary: { main: '#2db838', contrastText: '#fff' },
        secondary: { main: '#64748b' },
        background: { default: '#f4f7f9', paper: '#FFFFFF' },
        text: { primary: '#3A3B3C', secondary: '#5A5A5A' },
    },
    typography: {
        fontFamily: "'Inter', sans-serif",
        h4: { fontWeight: 700 },
        h5: { fontWeight: 600 },
        button: { textTransform: 'none', fontWeight: 600, borderRadius: 8 },
        ...appTypography,
    },
    shape: { borderRadius: 12 },
    components: {
        MuiStepIcon: {
            styleOverrides: {
                root: {
                    '&.Mui-active': { color: '#2db838' },
                    '&.Mui-completed': { color: '#259c30' }
                }
            }
        }
    }
});

// --- Helper Data & Functions ---
const helpContentData = {
    'RegistrationAssistance': {
        title: "Assistance with Registration",
        icon: <AssignmentTurnedInIcon />,
        youtubeId: null,
        checklist: null,
        detailsField: { name: 'issueDescription', label: 'Briefly describe the registration issue', required: true, multiline: true, rows: 4 }
    },
    'ApprovalRequest': {
        title: "Request for Approval",
        icon: <AssignmentTurnedInIcon />,
        youtubeId: null,
        checklist: null,
        detailsField: { name: 'listingName', label: 'What is the name of the listing/vehicle to approve?', required: true }
    },
    'CarHireHelp': {
        title: "Car Hire: Vehicle Not Showing",
        icon: <CarIcon />,
        youtubeId: 'ONwGzeZ6KNs',
        checklist: [
            "I have made the car available for hiring.",
            "The listing has been approved by the Abisiniya team."
        ],
        detailsField: { name: 'vehicleReg', label: 'What is the vehicle registration number?', required: true }
    },
    'CarShuttleHelp': {
        title: "Car Shuttle: Not Showing",
        icon: <ShuttleIcon />,
        youtubeId: 'ONwGzeZ6KNs',
        checklist: [
            "I have made the shuttle available for booking.",
            "The listing has been approved by the Abisiniya team."
        ],
        detailsField: { name: 'shuttleDetails', label: 'Which shuttle are you having an issue with?', required: true }
    },
    'BusHireHelp': {
        title: "Bus Hire: Not Showing",
        icon: <BusIcon />,
        youtubeId: 'ONwGzeZ6KNs',
        checklist: [
            "I have made the bus available for hiring.",
            "The listing has been approved by the Abisiniya team."
        ],
        detailsField: { name: 'busDetails', label: 'Which bus are you having an issue with?', required: true }
    },
    'ApartmentHelp': {
        title: "Apartment: Not Showing",
        icon: <ApartmentIcon />,
        youtubeId: 'PKgwd1djjvQ',
        checklist: [
            "I have added at least one 'Room Type' to the apartment.",
            "I have set the room type to be 'available for booking'.",
            "The listing has been approved by the Abisiniya team."
        ],
        detailsField: { name: 'apartmentName', label: 'What is the name of the apartment?', required: true }
    },
    'DirectChat': {
        title: "Direct General Inquiry",
        icon: <ChatIcon />,
        youtubeId: null,
        checklist: null,
        detailsField: { name: 'inquiry', label: 'How can we help you today?', required: true, multiline: true, rows: 4 }
    }
};

const formatTimestamp = (timestamp) => {
    if (!timestamp || !timestamp.toDate) {
        return '';
    }
    const date = timestamp.toDate();
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
};


// --- PRIORITY & DATE HELPERS (Copied from NewInquiryFlow) ---

const getRelevantTravelDate = (type, formData) => {
    if (!formData) return null;
    switch (type) {
        case 'travelInquiry': return formData.travelDate;
        case 'visaInquiry': return formData.travelDateVisa;
        case 'accommodationInquiry': return formData.checkInDate;
        case 'carHireInquiry': return formData.startDate;
        default: return null;
    }
};

const calculateLeadPriority = (dateString) => {
    // Default: Normal priority if no date provided
    if (!dateString) return { priority: 'normal', leadStatus: 'pipeline', isUrgent: false };

    const today = new Date();
    const travelDate = new Date(dateString);

    // Normalize to midnight for accurate day diff
    today.setHours(0, 0, 0, 0);
    travelDate.setHours(0, 0, 0, 0);

    const diffTime = travelDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // 1. URGENT (Today + 7 days)
    if (diffDays <= 7) {
        return { priority: 'high', leadStatus: 'urgent', isUrgent: true };
    }
    // 2. HOT LEAD (8 to 30 days)
    if (diffDays <= 30) {
        return { priority: 'medium', leadStatus: 'hot', isUrgent: false };
    }
    // 3. PIPELINE (> 30 days)
    return { priority: 'normal', leadStatus: 'pipeline', isUrgent: false };
};


// --- Custom Hook for Inquiry Flow Logic ---
const useInquiryFlow = (inquiryUserType) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [selectedInquiryType, setSelectedInquiryType] = useState(null);
    const [activeInquiryId, setActiveInquiryId] = useState(null);
    const [activeInquiryDetails, setActiveInquiryDetails] = useState(null);
    const [userId, setUserId] = useState(null);
    const [isAuthReady, setIsAuthReady] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [globalError, setGlobalError] = useState('');
    const appId = (typeof __app_id !== 'undefined') ? __app_id : 'default-app-id';

    useEffect(() => {
        const authSignIn = async () => {
            try {
                const initialAuthToken = (typeof __initial_auth_token !== 'undefined') ? __initial_auth_token : null;
                if (initialAuthToken) {
                    await signInWithCustomToken(firebaseAuthInstance, initialAuthToken);
                } else {
                    await signInAnonymously(firebaseAuthInstance);
                }
            } catch (error) {
                console.error("Authentication failed:", error);
                setGlobalError("Authentication failed. Please refresh and try again.");
            }
        };

        const unsubscribe = onAuthStateChanged(firebaseAuthInstance, user => {
            if (user) {
                setUserId(user.uid);
            }
            setIsAuthReady(true);
        });

        authSignIn();

        return () => unsubscribe();
    }, []);


    const handleInquiryTypeSelect = (inquiryTypeKey) => {
        setSelectedInquiryType(inquiryTypeKey);
        setCurrentStep(1);
        setGlobalError('');
    };

    // --- UPDATED INQUIRY SUBMISSION LOGIC (Matches Client Logic) ---
    const handleInquirySubmitSuccess = async (inquiryDataFromForm) => {
        if (!userId) {
            setGlobalError("Authentication not ready. Please try again.");
            return;
        }
        setIsLoading(true);
        setGlobalError('');

        try {
            // --- 1. CALCULATE PRIORITY ---
            const relevantDate = getRelevantTravelDate(selectedInquiryType, inquiryDataFromForm);
            const { priority, leadStatus, isUrgent } = calculateLeadPriority(relevantDate);

            // --- 2. PREPARE PAYLOAD ---
            const payload = {
                inquiryUserType: inquiryUserType,
                inquiryType: selectedInquiryType,
                formData: inquiryDataFromForm,
                // --- Send Flags to Backend API ---
                priority,       // 'high', 'medium', 'normal'
                leadStatus,     // 'urgent', 'hot', 'pipeline'
                isUrgent,       // boolean
                travelDate: relevantDate
            };

            // --- 3. SEND TO API (Using Axios 'api' instance) ---
            await api.post('/inquiries', payload);

            // --- 4. FIREBASE LOGIC ---
            const timestamp = serverTimestamp();
            const welcomeMessage = `Welcome ${inquiryDataFromForm.fullName}! A support agent will be with you shortly.`;

            const sanitizedFormData = Object.fromEntries(
                Object.entries(inquiryDataFromForm).map(([key, value]) => [key, value === undefined ? null : value])
            );

            const newInquiryId = `${Date.now()}-${userId.substring(0, 5)}`;

            // --- PRESERVED DATA STRUCTURE + PRIORITY FIELDS ---
            const commonInquiryData = {
                type: selectedInquiryType,
                userId,
                appId: appId,
                status: 'new_chat',
                createdAt: timestamp,
                updatedAt: timestamp,
                originalInquiryId: newInquiryId,
                formData: {
                    ...sanitizedFormData,
                    travelDate: relevantDate // Ensure dashboard always finds the date
                },
                inquiryUserType: inquiryUserType,
                lastMessageBy: 'user',
                lastMessageSnippet: "Inquiry created.",
                consultantUnreadCount: 1,
                userUnreadCount: 0,
                // --- Save to Firebase for Instant Dashboard Sorting ---
                priority,
                leadStatus,
                isUrgent
            };

            const batch = writeBatch(firestoreDbInstance);
            const userInquiryRef = doc(firestoreDbInstance, `artifacts/${appId}/users/${userId}/inquiries`, newInquiryId);
            batch.set(userInquiryRef, commonInquiryData);
            const globalInquiryDocRef = doc(firestoreDbInstance, "allInquiries", newInquiryId);
            batch.set(globalInquiryDocRef, commonInquiryData);
            const welcomeMessageRef = doc(collection(userInquiryRef, 'messages'));
            batch.set(welcomeMessageRef, {
                text: welcomeMessage,
                sender: 'system',
                senderType: 'system',
                timestamp: serverTimestamp(),
            });

            await batch.commit();

            setActiveInquiryId(newInquiryId);
            setActiveInquiryDetails({ ...commonInquiryData, createdAt: new Date() });
            setCurrentStep(2);

        } catch (error) {
            console.error("Error creating inquiry:", error);

            // Error Handling (Matches Client Logic)
            let errorMessage = error.message || "An unexpected error occurred. Please try again.";

            if (error.response && error.response.data) {
                const data = error.response.data;
                if (data.message) errorMessage = data.message;
                if (data.errors) {
                    errorMessage = Object.values(data.errors).flat().join(' ');
                }
            }

            setGlobalError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleBack = () => {
        setCurrentStep((prev) => prev - 1);
        if (currentStep === 1) setSelectedInquiryType(null);
        setGlobalError('');
    };

    const handleEndChatAndReset = async () => {
        if (activeInquiryId && userId && appId && firestoreDbInstance) {
            const timestamp = serverTimestamp();
            const updateData = {
                status: 'closed_by_provider',
                closedAt: timestamp,
                updatedAt: timestamp,
                lastMessageBy: 'user',
                lastMessageSnippet: 'Chat ended by Service Provider.'
            };
            try {
                const globalInquiryDocRef = doc(firestoreDbInstance, `allInquiries/${activeInquiryId}`);
                const userSpecificInquiryDocRef = doc(firestoreDbInstance, `artifacts/${appId}/users/${userId}/inquiries/${activeInquiryId}`);

                const batch = writeBatch(firestoreDbInstance);
                batch.update(globalInquiryDocRef, updateData);
                batch.update(userSpecificInquiryDocRef, updateData);
                await batch.commit();
            } catch (error) {
                console.error("Error updating inquiry to closed_by_provider:", error);
                setGlobalError("Failed to properly end the chat session. Please try starting a new inquiry.");
            }
        }

        setCurrentStep(0);
        setSelectedInquiryType(null);
        setActiveInquiryId(null);
        setActiveInquiryDetails(null);
        setGlobalError('');
    };

    return {
        currentStep, selectedInquiryType, activeInquiryId, activeInquiryDetails, userId, isAuthReady, isLoading, globalError,
        handleInquiryTypeSelect, handleInquirySubmitSuccess, handleBack, handleEndChatAndReset, appId
    };
};

// --- Child Components ---
const TroubleshootingStep = ({ inquiryType, onSubmit, onBack, isLoading }) => {
    const content = helpContentData[inquiryType];
    const [checkedState, setCheckedState] = useState({});

    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [phoneDialCode, setPhoneDialCode]       = useState('263'); // Zimbabwe default
    const [phoneCountryCode, setPhoneCountryCode] = useState('ZW');
    const [companyName, setCompanyName] = useState('');
    const [details, setDetails] = useState('');

    const [errors, setErrors] = useState({});

    const handleCheckboxChange = (event) => {
        setCheckedState({ ...checkedState, [event.target.name]: event.target.checked });
    };

    const allChecked = content.checklist ? content.checklist.every((_, index) => checkedState[`check${index}`]) : true;

    const validate = () => {
        const newErrors = {};
        if (!fullName.trim()) newErrors.fullName = 'Full Name is required.';
        if (!email.trim()) {
            newErrors.email = 'Email is required.';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
            newErrors.email = 'Please enter a valid email address (e.g., you@example.com)';
        }
        if (!phone.trim()) {
            newErrors.phone = 'Phone Number is required.';
        } else {
            const country = COUNTRY_PHONE_CODES.find(c => c.code === phoneCountryCode)
                         || COUNTRY_PHONE_CODES.find(c => c.dial === phoneDialCode);
            if (country) {
                const localDigits = phone.replace(/\D/g, '').slice(country.dial.length);
                if (localDigits.length < country.min || localDigits.length > country.max) {
                    const expected = country.min === country.max
                        ? `${country.min}`
                        : `${country.min}–${country.max}`;
                    newErrors.phone = `${country.name} (+${country.dial}) requires ${expected} digits — you entered ${localDigits.length}`;
                }
            }
        }
        if (!companyName.trim()) newErrors.companyName = 'Company Name is required.';
        if (content.detailsField.required && !details.trim()) {
            newErrors.details = 'This field is required.';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (!validate()) {
            return;
        }

        const formData = {
            fullName, email, phone, companyName,
            service: content.title,
            [content.detailsField.name]: details
        };

        onSubmit(formData);
    };

    return (
        <Box sx={{
            height: '100%',
            overflowY: 'auto',
            pr: { xs: 0, sm: 1 },
            '&::-webkit-scrollbar': {
                width: '8px',
            },
            '&::-webkit-scrollbar-track': {
                backgroundColor: '#f1f1f1',
                borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#c1c1c1',
                borderRadius: '4px',
                '&:hover': {
                    backgroundColor: '#a8a8a8',
                },
            },
        }}>
            <Typography variant="h5" align="center" gutterBottom sx={{ mb: 3 }}>
                {content.title}
            </Typography>

            {content.youtubeId && (
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                        <VideoLibraryIcon sx={{ mr: 1, color: 'primary.main' }} />
                        Tutorial Video
                    </Typography>
                    <Box sx={{
                        position: 'relative',
                        paddingTop: '56.25%',
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '1px solid #eee'
                    }}>
                        <iframe
                            src={`https://www.youtube.com/embed/${content.youtubeId}`}
                            frameBorder="0"
                            allow="autoplay; encrypted-media"
                            allowFullScreen
                            title="Help Video"
                            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                        />
                    </Box>
                </Box>
            )}

            {content.checklist && (
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                        <RuleIcon sx={{ mr: 1, color: 'primary.main' }} />
                        Troubleshooting Checklist
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        {content.checklist.map((text, index) => (
                            <FormControlLabel
                                key={index}
                                control={
                                    <Checkbox
                                        name={`check${index}`}
                                        onChange={handleCheckboxChange}
                                        color="primary"
                                    />
                                }
                                label={text}
                                sx={{ display: 'block', mb: 1 }}
                            />
                        ))}
                    </Paper>
                </Box>
            )}

            <Collapse in={allChecked}>
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                        <LiveHelpIcon sx={{ mr: 1, color: 'primary.main' }} />
                        Provide Your Details
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 3, borderTop: '3px solid', borderColor: 'primary.main', borderRadius: 2 }}>
                        <Alert
                            severity="warning"
                            icon={<WarningIcon />}
                            sx={{
                                mb: 2.5,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'warning.light',
                                bgcolor: '#fffbf0',
                                '& .MuiAlert-icon': { alignItems: 'center' },
                                '& .MuiAlert-message': { fontSize: '0.83rem', lineHeight: 1.6 }
                            }}
                        >
                            <strong>Please provide accurate contact details.</strong> Your <strong>email address</strong> and <strong>WhatsApp/phone number</strong> will be used by our team to follow up with you. Incorrect information may cause delays or missed responses.
                        </Alert>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Full Name"
                                    value={fullName}
                                    onChange={(e) => setFullName(e.target.value)}
                                    error={!!errors.fullName}
                                    helperText={errors.fullName}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Company Name"
                                    value={companyName}
                                    onChange={(e) => setCompanyName(e.target.value)}
                                    error={!!errors.companyName}
                                    helperText={errors.companyName}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    required
                                    type="email"
                                    label="Email Address"
                                    placeholder="e.g., name@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    error={!!errors.email}
                                    helperText={errors.email || 'Enter an email you actively check'}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <PhoneInputField
                                    value={phone}
                                    countryCode={phoneCountryCode}
                                    onChange={(fullNum, dialCode, countryCode) => {
                                        setPhone(fullNum);
                                        setPhoneDialCode(dialCode);
                                        setPhoneCountryCode(countryCode);
                                    }}
                                    error={!!errors.phone}
                                    helperText={errors.phone}
                                    required={true}
                                    label="WhatsApp / Phone Number"
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    required={content.detailsField.required}
                                    multiline={content.detailsField.multiline}
                                    rows={content.detailsField.rows || 1}
                                    label={content.detailsField.label}
                                    value={details}
                                    onChange={(e) => setDetails(e.target.value)}
                                    error={!!errors.details}
                                    helperText={errors.details}
                                    size="small"
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Box>
            </Collapse>

            <Box sx={{
                mt: 4,
                mb: 2,
                display: 'flex',
                justifyContent: 'space-between',
                position: 'sticky',
                bottom: 0,
                backgroundColor: 'background.paper',
                py: 2,
                borderTop: '1px solid #eee'
            }}>
                <Button
                    variant="outlined"
                    color="secondary"
                    onClick={onBack}
                    disabled={isLoading}
                    size="large"
                >
                    Back
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!allChecked || isLoading}
                    size="large"
                >
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Proceed to Live Chat'}
                </Button>
            </Box>
        </Box>
    );
};

const LiveChatView = ({ inquiryId, userId, onEndChat, db, appId }) => {
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [isSending, setIsSending] = useState(false);
    const messagesEndRef = useRef(null);
    const [chatError, setChatError] = useState(null);

    useEffect(() => {
        if (!db || !inquiryId || !userId) return;

        const messagesColRef = collection(db, "artifacts", appId, "users", userId, "inquiries", inquiryId, "messages");
        const q = query(messagesColRef, orderBy("timestamp"));

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const msgs = [];
            querySnapshot.forEach((doc) => {
                msgs.push({ id: doc.id, ...doc.data() });
            });
            setMessages(msgs);
        }, (error) => {
            console.error("Error fetching messages:", error);
            setChatError("Could not load messages. Please try refreshing.");
        });

        return () => unsubscribe();
    }, [db, inquiryId, appId, userId]);


    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !db) return;

        setIsSending(true);
        setChatError(null);

        try {
            const messagesColRef = collection(db, "artifacts", appId, "users", userId, "inquiries", inquiryId, "messages");
            await addDoc(messagesColRef, {
                text: newMessage.trim(),
                sender: userId,
                senderType: 'user',
                timestamp: serverTimestamp()
            });

            const updateData = {
                lastMessageSnippet: newMessage.trim().substring(0, 50),
                lastMessageBy: 'user',
                updatedAt: serverTimestamp(),
                status: 'active_chat',
            };

            const userInquiryRef = doc(db, "artifacts", appId, "users", userId, "inquiries", inquiryId);
            await updateDoc(userInquiryRef, updateData);

            const allInquiriesRef = doc(db, "allInquiries", inquiryId);
            await updateDoc(allInquiriesRef, updateData);

            setNewMessage('');

        } catch (error) {
            console.error("Error sending message:", error);
            setChatError("Failed to send message.");
        } finally {
            setIsSending(false);
        }
    };

    return (
        <Box sx={{
            height: '70vh',
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid #eee',
            borderRadius: 2,
            overflow: 'hidden'
        }}>
            {chatError && <Alert severity="error" sx={{ m: 2 }}>{chatError}</Alert>}

            <Paper
                elevation={0}
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    p: 2,
                    bgcolor: 'background.default',
                    '&::-webkit-scrollbar': {
                        width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: '#f1f1f1',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#c1c1c1',
                        borderRadius: '4px',
                        '&:hover': {
                            backgroundColor: '#a8a8a8',
                        },
                    },
                }}
            >
                {messages.map((msg) => (
                    <Box
                        key={msg.id}
                        sx={{
                            mb: 1.5,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: (msg.sender === userId || msg.senderType === 'user') ? 'flex-end' : 'flex-start'
                        }}
                    >
                        <Paper
                            elevation={1}
                            sx={{
                                p: '8px 12px',
                                borderRadius: (msg.sender === userId || msg.senderType === 'user') ? '10px 10px 0 10px' : '10px 10px 10px 0',
                                bgcolor: (msg.sender === userId || msg.senderType === 'user') ? 'primary.main' : 'grey.200',
                                color: (msg.sender === userId || msg.senderType === 'user') ? 'primary.contrastText' : 'text.primary',
                                maxWidth: '80%'
                            }}
                        >
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                                {msg.text}
                            </Typography>
                        </Paper>
                        <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, px: 0.5 }}>
                            {formatTimestamp(msg.timestamp)}
                        </Typography>
                    </Box>
                ))}
                <div ref={messagesEndRef} />
            </Paper>

            <Box sx={{ p: 2, borderTop: '1px solid #eee', bgcolor: 'background.paper' }}>
                <Box component="form" onSubmit={handleSendMessage} sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Type your message..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        disabled={isSending}
                    />
                    <IconButton
                        type="submit"
                        color="primary"
                        disabled={isSending || !newMessage.trim()}
                    >
                        <SendIcon />
                    </IconButton>
                </Box>
                <Button
                    variant="text"
                    color="secondary"
                    size="small"
                    onClick={onEndChat}
                    sx={{ mt: 1, display: 'block', mx: 'auto' }}
                    startIcon={<LogoutIcon />}
                >
                    End Chat & Start New
                </Button>
            </Box>
        </Box>
    );
};

// --- Main Application Component ---
export default function ServiceProviderInquiryFlow() {
    const {
        currentStep, selectedInquiryType, activeInquiryId, userId, isAuthReady, isLoading,
        globalError, handleInquiryTypeSelect, handleInquirySubmitSuccess, handleBack, handleEndChatAndReset, appId
    } = useInquiryFlow('service_provider_v2');

    const isMobile = useMediaQuery(inquiryFlowTheme.breakpoints.down('md'));
    const stepLabels = ['Select Topic', 'Provide Details', 'Live Chat'];

    if (!isAuthReady) {
        return (
            <ThemeProvider theme={inquiryFlowTheme}>
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }}>Connecting to support...</Typography>
                </Box>
            </ThemeProvider>
        );
    }

    const renderStepContent = () => {
        switch (currentStep) {
            case 0:
                return (
                    <Grid container spacing={15} alignItems="flex-start" sx={{ height: '100%', mt: '100px' }}>
                        <Grid item xs={12} md={15}>
                            <Typography variant="h6" gutterBottom>Support Menu</Typography>
                            <Paper
                                variant="outlined"
                                sx={{
                                    maxHeight: '60vh',
                                    overflowY: 'auto',
                                    '&::-webkit-scrollbar': {
                                        width: '8px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        backgroundColor: '#f1f1f1',
                                        borderRadius: '4px',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        backgroundColor: '#c1c1c1',
                                        borderRadius: '4px',
                                        '&:hover': {
                                            backgroundColor: '#a8a8a8',
                                        },
                                    },
                                }}
                            >
                                <List>
                                    <ListItemText
                                        primary="General"
                                        sx={{
                                            pl: 2,
                                            textTransform: 'uppercase',
                                            fontSize: '0.8rem',
                                            fontWeight: 'bold',
                                            color: 'text.secondary'
                                        }}
                                    />
                                    {Object.entries(helpContentData)
                                        .filter(([key]) => key.includes('Assistance') || key.includes('Request'))
                                        .map(([key, value]) => (
                                            <ListItem key={key} disablePadding>
                                                <ListItemButton onClick={() => handleInquiryTypeSelect(key)}>
                                                    <ListItemIcon sx={{ color: 'primary.main' }}>
                                                        {value.icon}
                                                    </ListItemIcon>
                                                    <ListItemText primary={value.title} />
                                                </ListItemButton>
                                            </ListItem>
                                        ))}
                                    <Divider sx={{ my: 1 }} />
                                    <ListItemText
                                        primary="Service Help"
                                        sx={{
                                            pl: 2,
                                            textTransform: 'uppercase',
                                            fontSize: '0.8rem',
                                            fontWeight: 'bold',
                                            color: 'text.secondary'
                                        }}
                                    />
                                    {Object.entries(helpContentData)
                                        .filter(([key]) => key.includes('Help'))
                                        .map(([key, value]) => (
                                            <ListItem key={key} disablePadding>
                                                <ListItemButton onClick={() => handleInquiryTypeSelect(key)}>
                                                    <ListItemIcon sx={{ color: 'primary.main' }}>
                                                        {value.icon}
                                                    </ListItemIcon>
                                                    <ListItemText primary={value.title} />
                                                </ListItemButton>
                                            </ListItem>
                                        ))}
                                    <Divider sx={{ my: 1 }} />
                                    <ListItem key="DirectChat" disablePadding>
                                        <ListItemButton onClick={() => handleInquiryTypeSelect('DirectChat')}>
                                            <ListItemIcon sx={{ color: 'primary.main' }}>
                                                <ChatIcon />
                                            </ListItemIcon>
                                            <ListItemText primary="Direct Live Chat" />
                                        </ListItemButton>
                                    </ListItem>
                                </List>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={7} sx={{ display: { xs: 'none', md: 'block' } }}>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                height: '100%',
                                textAlign: 'center',
                                p: 2,
                                bgcolor: 'grey.100',
                                borderRadius: 2,
                                mt: 6,
                                pt: 10
                            }}>
                                <Box
                                    component="img"
                                    sx={{
                                        height: 120,
                                        width: 120,
                                        mb: 2,
                                        alignItems: 'center',
                                    }}
                                    alt="Company Logo"
                                    src="/pics/logo.png"
                                    onError={(e) => { e.target.onerror = null; e.target.style.display = 'none' }}
                                />
                                <Typography variant="h5" sx={{ mb: 2 }}>
                                    Welcome to the Support Hub
                                </Typography>
                                <Typography color="text.secondary">
                                    Please select a topic from the menu to get started.
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                );
            case 1:
                return (
                    <Box sx={{ height: '70vh', overflow: 'hidden' }}>
                        <TroubleshootingStep
                            inquiryType={selectedInquiryType}
                            onSubmit={handleInquirySubmitSuccess}
                            onBack={handleBack}
                            isLoading={isLoading}
                        />
                    </Box>
                );
            case 2:
                return (
                    <LiveChatView
                        inquiryId={activeInquiryId}
                        userId={userId}
                        onEndChat={handleEndChatAndReset}
                        db={firestoreDbInstance}
                        appId={appId}
                    />
                );
            default:
                return <Typography>Something went wrong.</Typography>;
        }
    };

    return (
        <ThemeProvider theme={inquiryFlowTheme}>
            <Box sx={{
                width: '100%',
                minHeight: '100vh',
                py: { xs: 2, sm: 4 },
                bgcolor: 'background.default',
                overflowX: 'hidden'
            }}>
                <Paper sx={{
                    width: '100%',
                    maxWidth: '1200px',
                    mx: 'auto',
                    p: { xs: 2, sm: 3, md: 4 },
                    minHeight: '80vh'
                }}>
                    <Typography variant="h4" component="h1" align="center" sx={{ mb: 3 }}>
                        Service Provider Support
                    </Typography>

                    {globalError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {globalError}
                        </Alert>
                    )}

                    <Stepper activeStep={currentStep} alternativeLabel={!isMobile} sx={{ mb: 4 }}>
                        {stepLabels.map(label => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>

                    {currentStep === 2 && (
                        <Typography variant="h6" align="center" sx={{ mb: 2 }}>
                            Chatting with Support Team
                        </Typography>
                    )}

                    <Box sx={{ mt: 2, px: { xs: 0, sm: 2 } }}>
                        {isLoading && currentStep !== 2 ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            renderStepContent()
                        )}
                    </Box>
                </Paper>
            </Box>
        </ThemeProvider>
    );
}